"use client";

import { useGenerationStore } from '@/store/generation-store';
import { ReactNode, useEffect } from 'react';
import { usePathname } from 'next/navigation';

// Routes that DO need the generation store hydrated before first paint: the
// generator surfaces restore their in-flight jobs and last settings from
// localStorage, and rendering them empty first makes the UI jump.
//
// This is a whitelist on purpose. It used to be a blacklist ("skip for these few
// paths"), which meant every OTHER page — /prompt-library (397 URLs), /docs,
// /instrumenty, /promty — was served to crawlers as the spinner below instead of
// its content: the store can never be hydrated during SSR, so `hasHydrated` is
// always false on the server. Those pages don't read the store at all, so they
// now render straight through and ship real HTML.
const NEEDS_HYDRATION_PREFIXES = [
    '/create',
    '/image-generation',
    '/video-generation',
    '/apps/',
    '/image/',
    '/history',
    '/assets',
];

export function HydrationGuard({ children }: { children: ReactNode }) {
    const hasHydrated = useGenerationStore((state) => state._hasHydrated);
    const pathname = usePathname();

    useEffect(() => {
        useGenerationStore.persist.rehydrate();
    }, []);

    const needsHydration = !!pathname && NEEDS_HYDRATION_PREFIXES.some(p => pathname.startsWith(p));

    if (needsHydration && !hasHydrated) {
        return (
            <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background">
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <div className="h-40 w-40 rounded-full bg-primary/10 blur-3xl" />
                </div>
                <div className="relative h-14 w-14 rounded-full border-2 border-primary/20 border-t-primary shadow-[0_0_36px_rgba(99,102,241,0.35)] animate-spin" />
                </div>
        );
    }

    return <>{children}</>;
}
